import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as pluginsApi from '../api/plugins';

export const usePlugins = () => {
  return useQuery({
    queryKey: ['plugins'],
    queryFn: () => pluginsApi.getPlugins(),
  });
};

/**
 * Check if a specific plugin is enabled
 * @param {string} pluginName - The name of the plugin to check
 * @returns {{ isEnabled: boolean, isLoading: boolean, plugin: object|null }}
 */
export const usePluginEnabled = (pluginName) => {
  const { data, isLoading } = usePlugins();

  const plugin = data?.plugins?.find(p => p.name === pluginName) || null;
  const isEnabled = plugin?.enabled ?? false;

  return { isEnabled, isLoading, plugin };
};

export const usePlugin = (name) => {
  return useQuery({
    queryKey: ['plugin', name],
    queryFn: () => pluginsApi.getPlugin(name),
    enabled: !!name,
  });
};

export const useEnablePlugin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: pluginsApi.enablePlugin,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plugins'] });
    },
  });
};

export const useDisablePlugin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: pluginsApi.disablePlugin,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plugins'] });
    },
  });
};

export const useUpdatePluginConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ name, config }) => pluginsApi.updatePluginConfig(name, config),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['plugins'] });
      queryClient.invalidateQueries({ queryKey: ['plugin', variables.name] });
    },
  });
};
